

export enum Align {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right"
}

export const drawText = (
  ctx: CanvasRenderingContext2D,
  fontSizeRaw: number,
  text: string,
  top: number,
  hue: number,
  saturation: number,
  lightness: number,
  shiny: boolean,
  outsetBorder: boolean,
  x: number = 0,
  maxWidth: number = Number.MAX_SAFE_INTEGER,
  align: Align = Align.CENTER,
  reduceFontSizeAutomatically: boolean = false,
  bold: boolean = false
): DOMRect => {
  ctx.save();
  var fontSize = fontSizeRaw;
  ctx.font = `${bold ? 'bold ' : ''}${fontSize}px serif`;
  var textMetrics = ctx.measureText(text);
  if (reduceFontSizeAutomatically) {
    const factor = maxWidth / (textMetrics.actualBoundingBoxLeft + textMetrics.actualBoundingBoxRight);
    if (factor < 1) {
      fontSize = Math.round(factor * fontSize);
      ctx.font = `${fontSize}px serif`;
      textMetrics = ctx.measureText(text);
    }
  }
  const ascent = fontSize - textMetrics.fontBoundingBoxDescent;
  const baselineY = top + ascent;
  const textWidth = textMetrics.actualBoundingBoxLeft + textMetrics.actualBoundingBoxRight;
  const constrainedWidth = Math.min(textWidth, maxWidth);
  const left = align === Align.LEFT
    ? x
    : align === Align.RIGHT
    ? x - constrainedWidth
    : x - 0.5 * constrainedWidth;
  if (outsetBorder) {
    ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${Math.round(lightness / 3)}%)`; // '#2C1C02';
    //ctx.fillText(text, left + 2, baselineY + 2, maxWidth);
    //ctx.fillText(text, left + 1, baselineY + 4, maxWidth);
    //ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${1.74 * lightness}%)`; // '#E5910B';
    ctx.fillStyle = `hsl(${hue}, ${saturation}%, 100%)`; // '#E5910B';
    ctx.fillText(text, left - 1, baselineY - 1, maxWidth);
  }
  if (shiny) {
    const bgradient = ctx.createLinearGradient(
      x, baselineY - textMetrics.actualBoundingBoxAscent, 
      x, baselineY + textMetrics.actualBoundingBoxDescent
    );
    /*
    bgradient.addColorStop(0, `hsl(${hue}, ${saturation}%, ${lightness + 20}%)`); // '#E5910B'); // gold
    bgradient.addColorStop(0.33, `hsl(${hue}, ${saturation}%, ${lightness + 15}%)`); // '#B47208'); // orange
    bgradient.addColorStop(0.67, `hsl(${hue}, ${saturation}%, ${lightness + 5}%)`); // '#B47208'); // orange
    bgradient.addColorStop(1, `hsl(${hue}, ${saturation}%, ${lightness}%)`); // '#825206'); // original brown
    */
    bgradient.addColorStop(0, `hsl(${hue}, ${0.7 * saturation}%, ${0.5 * 1.74 * lightness}%)`); // '#E5910B'); // gold
    bgradient.addColorStop(0.33, `hsl(${hue}, ${0.7 * saturation}%, ${0.5 * 1.56 * lightness}%)`); // '#B47208'); // orange
    bgradient.addColorStop(0.67, `hsl(${hue}, ${0.7 * saturation}%, ${0.5 * 1.22 * lightness}%)`); // '#B47208'); // orange
    bgradient.addColorStop(1, `hsl(${hue}, ${0.7 * saturation}%, ${0.5 * lightness}%)`); // '#825206'); // original brown
    ctx.fillStyle = bgradient;
  } else {
    ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }
  ctx.fillText(text, left, baselineY, maxWidth);
  ctx.restore();
  return new DOMRect(
    left, top, 
    constrainedWidth, ascent + textMetrics.fontBoundingBoxDescent
  );
}

export const drawMultilineText = (
  ctx: CanvasRenderingContext2D,
  fontSize: number,
  text: string,
  baselineY: number,
  hue: number,
  saturation: number,
  lightness: number,
  shiny: boolean,
  outsetBorder: boolean,
  x: number = 0,
  maxWidth: number = Number.MAX_SAFE_INTEGER,
  align: Align = Align.CENTER,
  lineHeight: number = 1.4,
  tryShrinkWidth: boolean = false,
): [DOMRect, number] => {
  ctx.font = `${fontSize}px serif`;

  const words = text.split(' ');

  const breakText = (availableWidth: number) => {
    const lines: string[] = [];
    let currentLine = '';
    let startIndex = 0;

    words.forEach((word, index) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const textMetrics = ctx.measureText(testLine);
      const testWidth = textMetrics.actualBoundingBoxLeft + textMetrics.actualBoundingBoxRight;
    
      if (testWidth > availableWidth && index > 0) {
        const breakIndex = word.indexOf('​');
        if (breakIndex > 0) {
          const preBreak = word.substring(0, breakIndex);
          const postBreak = word.substring(breakIndex + 1);
          const testLineBreakWord = currentLine ? `${currentLine} ${preBreak}-` : (preBreak + '-');
          const textMetricsBreakWord = ctx.measureText(testLineBreakWord);
          const testWidthBreakWord = textMetricsBreakWord.actualBoundingBoxLeft + textMetricsBreakWord.actualBoundingBoxRight;
          if (testWidthBreakWord <= availableWidth) {
            lines.push(testLineBreakWord);
            startIndex += testLineBreakWord.length;
            currentLine = postBreak;
          } else {
            lines.push(currentLine);
            startIndex += currentLine.length + 1;
            currentLine = word;  
          }
        } else {
          lines.push(currentLine);
          startIndex += currentLine.length + 1;
          currentLine = word;
        }
      } else {
        currentLine = testLine;
      }
    });
    lines.push(currentLine);
    return lines;
  }

  var lines = breakText(maxWidth);
  var adjustedWidth = maxWidth;
  if (tryShrinkWidth) {
    for (let factor = 0.95; factor >= 0.6; factor -= 0.05) {
      const alines = breakText(maxWidth * factor);
      if (alines.length !== lines.length) {
        break;
      }
      lines = alines;
      adjustedWidth = maxWidth * factor;
    }
  }

  const bbox = new DOMRect(x, baselineY, 0, 0);
  lines.forEach((line, index) => {
    const lbox = drawText(ctx, fontSize, line, baselineY + index * fontSize * lineHeight, hue, saturation, lightness, shiny, outsetBorder, x, adjustedWidth, align);
    if (lbox.x < bbox.x) {
      bbox.width += bbox.x - lbox.x;
      bbox.x = lbox.x;
    }
    if (lbox.y < bbox.y) {
      bbox.height += bbox.y - lbox.y;
      bbox.y = lbox.y;
    }
    bbox.width = Math.max(bbox.width, lbox.x + lbox.width - bbox.x);
    bbox.height = Math.max(bbox.height, lbox.y + lbox.height - bbox.y);
  });
  return [bbox, lines.length];
}